import {
  Flex,
  Text,
  Button,
  Spinner,
  useToast,
  HStack,
} from "@chakra-ui/react";
import styled from "@emotion/styled";
import { WalletImportMethod } from "@syla/shared/types/models/WalletBase";
import React, { useContext, useMemo } from "react";
import { MdSync, MdOutlineUpload } from "react-icons/md";

import { ModalContext } from "../../../../../contexts/ModalContext";
import { NumberStyler } from "../../../../../helper/NumberStyler";

import { buildSyncError } from "../../../../../helper/wallet/buildSyncError";
import { sortAssetHoldings } from "../../../../../helper/wallet/sortAssetHoldings";
import { useValueHoldings } from "../../../../../store/useValueHoldings";
import { reSyncWallet } from "../../../../../store/actions/wallet";
import { WalletResponse } from "../../../../../types/wallet/wallet";
import { Divider } from "../../../../atoms/Texts";
import { getDataSourceStatusProps } from "../../getDataSourceStatusProps";
import { LastSyncedTime } from "../../LastSyncedTime";
import { HoldingTable } from "./HoldingTable";
import { WalletDetails } from "./WalletDetails";

export const DataSourceExpanded = ({
  walletResponse,
  openEditDrawer,
  allowSync,
  allowEdit,
}: {
  walletResponse: WalletResponse;
  allowSync: boolean;
  allowEdit: boolean;
  openEditDrawer?: () => void;
}): JSX.Element => {
  const toast = useToast();

  // get everything out of the modal context
  const { openModal, closeModal } = useContext(ModalContext);

  const { data: holdingsValue, isLoading: valueHoldingsLoading } =
    useValueHoldings();
  const totalValue = useMemo(
    () =>
      holdingsValue?.getWalletTotals({
        walletId: walletResponse._id,
      })?.marketValue,
    [holdingsValue, walletResponse._id]
  );

  // skip negative holdings when calculating allocation %
  const allocationTotalValue = useMemo(
    () =>
      holdingsValue?.getWalletTotals({
        walletId: walletResponse._id,
        skipNegative: true,
      })?.marketValue,
    [holdingsValue, walletResponse._id]
  );

  // re-sync wallet handler
  const reSyncWalletHandler = async () => {
    // check if the wallet was imported via api
    if (!allowSync) return;

    openModal({
      type: "component",
      heading: "Syncing",
      contents: ["This may take a moment"],
      component: (
        <Flex direction="column" alignItems="center" justifyContent="center">
          <Spinner
            color="red.500"
            emptyColor="red.200"
            my="10px"
            thickness="5px"
            size="xl"
          />
        </Flex>
      ),
      hideBottomButton: true,
    });

    try {
      await reSyncWallet([walletResponse._id]);
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.log(error);
      toast(buildSyncError(error));
    } finally {
      closeModal();
      // invalidated here so that the last synced display updates after the modal closes
    }
  };

  const statusProps = getDataSourceStatusProps(walletResponse);

  return (
    <HStack
      bgColor="white.500"
      direction="row"
      p="20px"
      spacing={0}
      flexShrink={0}
      flexGrow={1}
      alignItems="flex-start"
    >
      <Flex flexGrow={1} flexShrink={0} flexBasis="1px" direction="column">
        <TitleText>Status</TitleText>
        <Flex direction="row" alignItems="flex-start" mb="10px">
          {walletResponse.importMethod == WalletImportMethod.API ? (
            <MdSync size="1.1rem" color="#0BC196" />
          ) : (
            <MdOutlineUpload size="1.1rem" color="#2A84FF" />
          )}

          <Flex direction="column" ml="10px">
            <Text color="black.600" fontSize="0.875rem">
              <Text
                color={statusProps.color}
                pr="5px"
                fontSize="0.75rem"
                textTransform="uppercase"
              >
                {statusProps.status}
              </Text>
              <LastSyncedTime
                importMethod={walletResponse.importMethod}
                lastUpdated={statusProps.lastUpdated}
              />
            </Text>
          </Flex>
        </Flex>
        {/* only show the sync button if the wallet was imported via api */}
        {allowSync && (
          <RedBtn onClick={reSyncWalletHandler} w="70px" mb="20px">
            Sync
          </RedBtn>
        )}
        <TitleText>Wallet Details</TitleText>
        <WalletDetails walletResponse={walletResponse} />
        {allowEdit && (
          <RedBtn onClick={openEditDrawer} w="100px">
            Edit
          </RedBtn>
        )}
      </Flex>
      <Flex
        flexGrow={2}
        flexShrink={0}
        flexBasis="1px"
        direction="column"
        mx="20px"
      >
        <Flex direction="row">
          <TitleText>Holdings</TitleText>
          <Divider m="0 10px" />
          {/* eslint-disable-next-line no-constant-condition */}
          {!walletResponse.holdings || valueHoldingsLoading ? (
            <Text>Calculating...</Text>
          ) : (
            <NumberStyler
              num={totalValue}
              unit="currency"
              fontWeight="600"
              fontSize="0.875rem"
              negativeColored
            />
          )}
        </Flex>
        {walletResponse.holdings && (
          <HoldingTable
            wallet={walletResponse}
            holdings={sortAssetHoldings(walletResponse.holdings)}
            totalHoldingValue={allocationTotalValue}
          />
        )}
      </Flex>
    </HStack>
  );
};

const TitleText = styled(Text)`
  color: #000;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 10px;
`;

const RedBtn = styled(Button)`
  border-color: #ff3600;
  color: #ff3600;
  font-size: 0.75rem;
  font-weight: 600;
  height: 30px;
  background-color: transparent;
  border-width: 1px;
  padding: 0 10px;
  &:hover {
    background-color: transparent;
  }
  &:focus {
    border-color: #ff3600;
  }
  &:active {
    border-color: #ff3600;
    background-color: #ffd7cc;
  }
`;
